import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Upload, Download, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Site {
  id: string;
  location_name: string;
}

interface ParsedRow {
  full_name: string;
  display_name: string;
  email: string;
  password: string;
  mobile_phone: string;
  position: string;
  team: string;
  role: string;
  site: string;
  siteId?: string;
  valid: boolean;
  errors: string[];
}

interface UploadResult {
  row: number;
  email: string;
  success: boolean;
  error?: string;
}

interface BulkUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sites: Site[];
  onComplete: () => void;
}

const TEMPLATE_HEADERS = ['full_name', 'display_name', 'email', 'password', 'mobile_phone', 'position', 'team', 'role', 'site'];
const VALID_ROLES = ['user', 'supervisor', 'site_admin', 'admin'];

function parseCSV(text: string): string[][] {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  return lines.map(line => {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    cells.push(current.trim());
    return cells;
  });
}

export function BulkUploadDialog({ open, onOpenChange, sites, onComplete }: BulkUploadDialogProps) {
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [phase, setPhase] = useState<'upload' | 'preview' | 'done'>('upload');
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const downloadTemplate = () => {
    const sampleRow = ['John Doe', 'John', 'john@company.com', 'Pass123!', '+628123456789', 'Operator', 'HSE', 'user', sites[0]?.location_name || 'Site A'];
    const csv = [TEMPLATE_HEADERS.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parsed = parseCSV(text);
      if (parsed.length < 2) {
        toast({ title: 'Invalid file', description: 'CSV must have a header row and at least one data row', variant: 'destructive' });
        return;
      }
      const headers = parsed[0].map(h => h.toLowerCase().replace(/\s+/g, '_'));
      const dataRows = parsed.slice(1);
      const siteMap = new Map(sites.map(s => [s.location_name.toLowerCase(), s.id]));

      const parsedRows: ParsedRow[] = dataRows.map(cells => {
        const row: any = {};
        TEMPLATE_HEADERS.forEach((h, i) => { row[h] = cells[i] || ''; });
        const errors: string[] = [];
        if (!row.full_name) errors.push('Missing full_name');
        if (!row.display_name) errors.push('Missing display_name');
        if (!row.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) errors.push('Invalid email');
        if (!row.password || row.password.length < 6) errors.push('Password < 6 chars');
        if (!row.team) errors.push('Missing team');
        if (!row.role || !VALID_ROLES.includes(row.role)) errors.push('Invalid role');
        const siteId = siteMap.get(row.site?.toLowerCase());
        if (row.site && !siteId) errors.push(`Site "${row.site}" not found`);
        return { ...row, siteId: siteId || undefined, valid: errors.length === 0, errors };
      });

      setRows(parsedRows);
      setPhase('preview');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleUploadAll = async () => {
    setUploading(true);
    setProgress(0);
    const uploadResults: UploadResult[] = [];
    const validRows = rows.filter(r => r.valid);

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        const { data, error } = await supabase.functions.invoke('create-user', {
          body: {
            email: row.email,
            password: row.password,
            fullName: row.full_name,
            displayName: row.display_name,
            position: row.position || null,
            team: row.team,
            role: row.role,
            mobilePhone: row.mobile_phone || null,
            siteId: row.siteId || null,
          },
        });
        if (error || !data?.success) {
          uploadResults.push({ row: i + 1, email: row.email, success: false, error: data?.error || error?.message || 'Unknown error' });
        } else {
          uploadResults.push({ row: i + 1, email: row.email, success: true });
        }
      } catch (err: any) {
        uploadResults.push({ row: i + 1, email: row.email, success: false, error: err.message });
      }
      setProgress(Math.round(((i + 1) / validRows.length) * 100));
    }

    setResults(uploadResults);
    setUploading(false);
    setPhase('done');
    onComplete();
  };

  const reset = () => {
    setRows([]);
    setResults([]);
    setPhase('upload');
    setProgress(0);
  };

  const validCount = rows.filter(r => r.valid).length;
  const invalidCount = rows.filter(r => !r.valid).length;
  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Upload Users</DialogTitle>
          <DialogDescription>Upload a CSV file to create multiple users at once</DialogDescription>
        </DialogHeader>

        {phase === 'upload' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
              <Button onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" />
                Select CSV File
              </Button>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
            </div>
            <p className="text-sm text-muted-foreground">
              Headers: full_name, display_name, email, password, mobile_phone, position, team, role, site.<br />
              Valid roles: user, supervisor, site_admin, admin. Site must match an existing site name.
            </p>
          </div>
        )}

        {phase === 'preview' && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Badge variant="secondary">{validCount} valid</Badge>
              {invalidCount > 0 && <Badge variant="destructive">{invalidCount} invalid</Badge>}
              <span className="text-sm text-muted-foreground">{rows.length} total rows</span>
            </div>

            <div className="rounded-md border max-h-[40vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={i} className={!row.valid ? 'bg-destructive/5' : ''}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{row.full_name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.mobile_phone || '-'}</TableCell>
                      <TableCell>{row.team}</TableCell>
                      <TableCell>{row.role}</TableCell>
                      <TableCell>{row.site || '-'}</TableCell>
                      <TableCell>
                        {row.valid ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <span className="text-xs text-destructive">{row.errors.join('; ')}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {uploading && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-muted-foreground text-center">Uploading... {progress}%</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} disabled={uploading}>Back</Button>
              <Button onClick={handleUploadAll} disabled={uploading || validCount === 0}>
                {uploading ? 'Uploading...' : `Upload ${validCount} Users`}
              </Button>
            </div>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Badge variant="secondary">{successCount} created</Badge>
              {failCount > 0 && <Badge variant="destructive">{failCount} failed</Badge>}
            </div>

            {failCount > 0 && (
              <div className="rounded-md border max-h-[30vh] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.filter(r => !r.success).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.row}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell className="text-destructive text-sm">{r.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <Button onClick={() => { reset(); onOpenChange(false); }}>Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
